/**
 * X-Domain storage configuration state
 * @module
 */
var _ = require('optimizely-client-fn');
var Logger = require('core/utils/logger');

var actionTypes = require('../constants/action_types');

var MAX_QUEUED_MESSAGES = __MAX_QUEUED_MESSAGES__;

/**
 * @typedef {Object} XDomainMessage
 * @property {Object} data
 * @property {Function} [resolver]
 * @property {number} [startTime]
 * @property {number} [endTime]
 */

/**
 * @typedef {Object} XDomainFrame
 * @property {number} id
 * @property {Object} target
 * @property {string} origin
 * @property {string} path
 */

module.exports = {
  initialize: function() {
    this.__state = {
      frames: [],
      defaultFrame: null,
      messages: [],
      subscribers: [],
      canonicalOrigins: null,
      disabled: false,
    };

    this.on(actionTypes.XDOMAIN_SET_DEFAULT_FRAME, this.__setDefaultFrame);
    this.on(actionTypes.XDOMAIN_ADD_FRAME, this.__addFrame);
    this.on(actionTypes.XDOMAIN_SET_MESSAGE, this.__setMessage);
    this.on(actionTypes.XDOMAIN_ADD_SUBSCRIBER, this.__addSubscriber);
    this.on(actionTypes.XDOMAIN_SET_CANONICAL_ORIGINS, this.__setCanonicalOrigins);
    this.on(actionTypes.XDOMAIN_SET_DISABLED, this.__disableXDomain);
  },

  /**
   *
   * @returns {module:stores/xdomain~XDomainMessage[]}
   */
  getMessages: function(): import('core/stores/xdomain').XDomainMessage[] {
    return _.cloneDeep(this.__state.messages);
  },

  /**
   * @returns {number} ID of oldest event in the queue
   */
  getOffset: function(): number {
    if (this.__state.messages.length === 0) {
      return 0;
    }
    return this.__state.messages[0].data.id;
  },

  /**
   * Get ID of next message to be added
   * @returns {number}
   */
  getNextMessageId: function(): number {
    return this.__state.messages.length + this.getOffset();
  },

  /**
   * Get a message, given its id
   * @param {number} messageId
   * @returns {module:stores/xdomain~XDomainMessage|undefined}
   */
  getMessageById: function(messageId:number): import('core/stores/xdomain').XDomainMessage|undefined {
    return this.__state.messages[messageId - this.getOffset()];
  },

  /**
   * Get list of subscribers
   * @returns {Function[]}
   */
  getSubscribers: function(): Function[] {
    return this.__state.subscribers;
  },

  /**
   * Get list of all frames
   * @returns {Object[]}
   */
  getFrames: function(): any[] {
    return this.__state.frames;
  },

  /**
   * Get ID of next frame to be added
   * @returns {number}
   */
  getNextFrameId: function(): number {
    return this.__state.frames.length;
  },

  /**
   * Get a default frame
   * @returns {module:stores/xdomain~XDomainFrame|undefined}
   */
  getDefaultFrame: function(): import('core/stores/xdomain').XDomainFrame|undefined {
    return this.__state.defaultFrame;
  },

  /**
   * Get canonical origins - origins passed in by user calling waitForOriginSync API
   * @returns {string[]|null}
   */
  getCanonicalOrigins: function(): string[]|null {
    return _.cloneDeep(this.__state.canonicalOrigins);
  },

  isDisabled: function() {
    return this.__state.disabled;
  },

  /**
   *
   * @param {module:stores/xdomain~XDomainFrame} payload
   * @private
   */
  __setDefaultFrame: function(payload:import('core/stores/xdomain').XDomainFrame) {
    this.__state.defaultFrame = payload;
  },

  /**
   *
   * @param {module:stores/xdomain~XDomainFrame} payload
   * @private
   */
  __addFrame: function(payload:import('core/stores/xdomain').XDomainFrame) {
    this.__state.frames.push(payload);
  },

  /**
   *
   * @param {Object} payload
   * @param {string} payload.messageId
   * @param {module:stores/xdomain~XDomainMessage} payload.message
   * @private
   */
  __setMessage: function(payload:any) {
    this.__state.messages[payload.messageId - this.getOffset()] = payload.message;
    // Clear old messages
    while (this.__state.messages.length > MAX_QUEUED_MESSAGES) {
      // shift over
      var deleted = this.__state.messages.shift();
      Logger.debug('XDomainStorage: Cleared old message: ' + deleted.data.id);
    }
  },

  /**
   *
   * @param {Object} payload
   * @param {Function} payload.subscriber
   * @private
   */
  __addSubscriber: function(payload:any) {
    this.__state.subscribers.push(payload.subscriber);
  },

  /**
   *
   * @param {Object} payload
   * @param {string[]} payload.canonicalOrigins
   * @private
   */
  __setCanonicalOrigins: function(payload:any) {
    this.__state.canonicalOrigins = payload.canonicalOrigins;
  },

  /**
   *
   * @param {Object} payload
   * @param {boolean} payload.disabled
   * @private
   */
  __disableXDomain: function(payload:any) {
    this.__state.disabled = payload.disabled;
  },
};
